import { del, get, getAll, put, putRecord, clearLabelCorpus, loadLabelCorpus, dspConfig, dspFingerprint, DEFAULT_LIBRARY_ID, qualifiedLabel, } from "@beatbox/voice-core";
import { loadCustomPhonemes } from "./phonemes.js";
import { buildBuiltinLibraries } from "./preset-libraries.js";
const LIB_STORE = "phoneme-libraries";
const ACTIVE_STORE = "active-library";
const ACTIVE_KEY = "active";
export const MIN_TRAINED_FRAMES = 20;
// Percussive/impulse sounds are captured as short bursts, so far fewer frames
// are collected per calibration. Use a lower threshold so pops can be marked
// as trained too.
export const MIN_TRAINED_FRAMES_PERCUSSIVE = 3;
const BUILTIN_DEFS = [
    {
        id: "hiss",
        label: "snake sound",
        icon: "🐍",
        percussive: false,
        pitchInvariant: true,
    },
    {
        id: "airy",
        label: "windy sound",
        icon: "🌬️",
        percussive: false,
        pitchInvariant: true,
    },
    {
        id: "pop",
        label: "pop sound",
        icon: "💥",
        percussive: true,
        pitchInvariant: true,
    },
    {
        id: "hum",
        label: "humming sound",
        icon: "🎵",
        percussive: false,
        pitchInvariant: true,
    },
];
function uuid() {
    if (typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return "lib-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
function liveFingerprint() {
    return dspFingerprint(dspConfig);
}
export async function ensureLibrariesMigrated() {
    const existing = await get(LIB_STORE, DEFAULT_LIBRARY_ID);
    if (!existing) {
        let custom = [];
        try {
            custom = await loadCustomPhonemes();
        }
        catch { }
        const now = Date.now();
        const phonemes = [
            ...BUILTIN_DEFS.map((d) => ({ ...d, trained: false })),
            ...custom.map((c) => ({
                id: c.id,
                label: c.label,
                icon: c.icon,
                percussive: !!c.percussive,
                pitchInvariant: c.pitchInvariant ?? true,
                trained: false,
            })),
        ];
        const lib = {
            id: DEFAULT_LIBRARY_ID,
            name: "Default",
            version: 1,
            builtin: true,
            createdAt: now,
            updatedAt: now,
            phonemes,
        };
        await putRecord(LIB_STORE, lib);
        console.info(`[libraries] migrated: created 'default' library with ` +
            `${phonemes.length} phoneme(s)`);
        await refreshTrainedFlags(DEFAULT_LIBRARY_ID);
    }
    await ensurePresetLibraries();
    const active = await get(ACTIVE_STORE, ACTIVE_KEY);
    if (!active) {
        await put(ACTIVE_STORE, ACTIVE_KEY, {
            activeLibraryId: DEFAULT_LIBRARY_ID,
        });
    }
}
async function ensurePresetLibraries() {
    const now = Date.now();
    const shipped = buildBuiltinLibraries(now);
    for (const preset of shipped) {
        const existing = await get(LIB_STORE, preset.id);
        if (!existing) {
            await putRecord(LIB_STORE, preset);
            console.info(`[libraries] seeded builtin preset '${preset.id}' ` +
                `(${preset.phonemes.length} phoneme(s))`);
            await refreshTrainedFlags(preset.id);
            continue;
        }
        if (!existing.builtin || existing.version >= preset.version) {
            continue;
        }
        const trainedIds = new Set(existing.phonemes
            .filter((p) => p.trained)
            .map((p) => p.id));
        const merged = {
            ...preset,
            createdAt: existing.createdAt,
            updatedAt: now,
            phonemes: preset.phonemes.map((p) => ({
                ...p,
                trained: trainedIds.has(p.id),
            })),
        };
        await putRecord(LIB_STORE, merged);
        console.info(`[libraries] upgraded builtin preset '${preset.id}' ` +
            `v${existing.version}→v${preset.version}`);
        await refreshTrainedFlags(preset.id);
    }
}
export async function listLibraries() {
    await ensureLibrariesMigrated();
    const all = await getAll(LIB_STORE);
    return all.sort((a, b) => {
        if (a.id === DEFAULT_LIBRARY_ID)
            return -1;
        if (b.id === DEFAULT_LIBRARY_ID)
            return 1;
        return a.name.localeCompare(b.name);
    });
}
export async function getLibrary(id) {
    return (await get(LIB_STORE, id)) ?? null;
}
export async function createLibrary(name, phonemes = []) {
    const now = Date.now();
    const lib = {
        id: uuid(),
        name: await dedupeName(name),
        version: 1,
        builtin: false,
        createdAt: now,
        updatedAt: now,
        phonemes: phonemes.map((p) => ({ ...p })),
    };
    await putRecord(LIB_STORE, lib);
    console.info(`[libraries] created library '${lib.name}' (${lib.id})`);
    return lib;
}
export async function updateLibrary(lib) {
    lib.updatedAt = Date.now();
    await putRecord(LIB_STORE, lib);
    console.debug(`[libraries] updated library '${lib.name}' (${lib.id})`);
}
export async function deleteLibrary(id) {
    const lib = await getLibrary(id);
    if (lib?.builtin) {
        throw new Error("cannot delete the built-in default library");
    }
    for (const p of lib?.phonemes ?? []) {
        await clearLabelCorpus(qualifiedLabel(id, p.id));
    }
    await del(LIB_STORE, id);
    console.info(`[libraries] deleted library '${id}'`);
    const activeId = await getActiveLibraryId();
    if (activeId === id) {
        await setActiveLibraryId(DEFAULT_LIBRARY_ID);
    }
}
export async function duplicateLibrary(id, newName) {
    const src = await getLibrary(id);
    if (!src)
        throw new Error(`library '${id}' not found`);
    const now = Date.now();
    const dup = {
        id: uuid(),
        name: await dedupeName(newName),
        version: 1,
        builtin: false,
        createdAt: now,
        updatedAt: now,
        phonemes: src.phonemes.map((p) => ({ ...p, trained: false })),
    };
    await putRecord(LIB_STORE, dup);
    console.info(`[libraries] duplicated '${id}' → '${dup.name}' (${dup.id})`);
    return dup;
}
export async function getActiveLibraryId() {
    await ensureLibrariesMigrated();
    const active = await get(ACTIVE_STORE, ACTIVE_KEY);
    const id = active?.activeLibraryId ?? DEFAULT_LIBRARY_ID;
    const lib = await getLibrary(id);
    if (!lib) {
        console.warn(`[libraries] active id '${id}' missing; falling back to default`);
        await put(ACTIVE_STORE, ACTIVE_KEY, {
            activeLibraryId: DEFAULT_LIBRARY_ID,
        });
        return DEFAULT_LIBRARY_ID;
    }
    return id;
}
export async function setActiveLibraryId(id) {
    await put(ACTIVE_STORE, ACTIVE_KEY, { activeLibraryId: id });
    console.info(`[libraries] active library set to '${id}'`);
}
export async function getActiveLibrary() {
    const id = await getActiveLibraryId();
    const lib = await getLibrary(id);
    if (lib)
        return lib;
    await ensureLibrariesMigrated();
    return (await getLibrary(DEFAULT_LIBRARY_ID));
}
export async function exportLibrary(id) {
    const lib = await getLibrary(id);
    if (!lib)
        throw new Error(`library '${id}' not found`);
    return JSON.stringify(lib, null, 2);
}
export async function importLibrary(json) {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed.phonemes)) {
        throw new Error("invalid library JSON: missing phonemes array");
    }
    const sanitize = (p) => ({
        id: (p.id ?? "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9_:-]/g, ""),
        label: (p.label ?? "").trim() || (p.id ?? "").trim(),
        icon: (p.icon ?? "").trim() || "❓",
        percussive: !!p.percussive,
        pitchInvariant: p.pitchInvariant ?? true,
        // Imported JSON carries no corpora → always untrained.
        trained: false,
    });
    const now = Date.now();
    const lib = {
        id: uuid(),
        name: await dedupeName((parsed.name ?? "Imported Library").trim() || "Imported Library"),
        version: 1,
        builtin: false,
        createdAt: now,
        updatedAt: now,
        phonemes: parsed.phonemes.map(sanitize).filter((p) => p.id.length > 0),
    };
    await putRecord(LIB_STORE, lib);
    console.info(`[libraries] imported library '${lib.name}' (${lib.id}) with ` +
        `${lib.phonemes.length} phoneme(s)`);
    return lib;
}
export async function refreshTrainedFlags(id) {
    const lib = await getLibrary(id);
    if (!lib)
        throw new Error(`library '${id}' not found`);
    const liveFp = liveFingerprint();
    let changed = false;
    for (const p of lib.phonemes) {
        const corpus = await loadLabelCorpus(qualifiedLabel(id, p.id));
        const minFrames = p.percussive
            ? MIN_TRAINED_FRAMES_PERCUSSIVE
            : MIN_TRAINED_FRAMES;
        const trained = !!corpus &&
            corpus.rows.length >= minFrames &&
            corpus.dspFingerprint !== null &&
            corpus.dspFingerprint === liveFp;
        if (p.trained !== trained) {
            p.trained = trained;
            changed = true;
        }
    }
    if (changed) {
        await updateLibrary(lib);
        console.info(`[libraries] refreshed trained flags for '${id}' ` +
            `(${lib.phonemes.filter((p) => p.trained).length}/` +
            `${lib.phonemes.length} trained)`);
    }
    return lib;
}
export async function markPhonemeTrainedInActive(phonemeId, trained = true) {
    const lib = await getActiveLibrary();
    const p = lib.phonemes.find((x) => x.id === phonemeId);
    if (!p)
        return;
    if (p.trained !== trained) {
        p.trained = trained;
        await updateLibrary(lib);
    }
}
async function dedupeName(name) {
    const all = await getAll(LIB_STORE);
    const taken = new Set(all.map((l) => l.name.toLowerCase()));
    if (!taken.has(name.toLowerCase()))
        return name;
    let n = 2;
    while (taken.has(`${name} ${n}`.toLowerCase()))
        n++;
    return `${name} ${n}`;
}
